import React, { FC, useCallback } from "react";
import { getProjectName } from "ducks/project/selectors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { useProjectPreviewUrl } from "../../../../../hooks/preview/usePreviewUrl";
import { useOpenProject } from "../../../../../hooks/project/useOpenProject";
import { formatDate } from "../../../../../utils/date";
import { formatNumber } from "../../../../../utils/formatNumber";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { DialogTitle } from "../../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../../DialogComponents/FullScreenDialog";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";
import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { DataField, FieldValue, styledHeaderText } from "../../../styled";
import { useDialogReset } from "../../../../../hooks/useDialogs";
import { useCurrentResource, useLayerCount } from "../../hooks/useProjectInfo";

export const ProjectInfo: FC = () => {
  const { t } = useTranslation();
  const projectIsOpened = !!useSelector(getProjectName);
  const project = useCurrentResource();
  const { alias, name, description, preview, owner, changedDate } = project;
  const openProject = useOpenProject(name);
  const cancelDialog = useDialogReset();
  const layerCount = useLayerCount(name);
  const previewUrl = useProjectPreviewUrl(name);

  const onOpen = useCallback(() => {
    openProject();
    cancelDialog();
  }, [cancelDialog, openProject]);

  return (
    <FullScreenDialog
      dialogContext={{
        title: {
          icon: "info",
        },
        applyText: t("actions.ok", { ns: "common" }),
        meta: {
          aliasLabel: t("infoResource.projectName", { ns: "dialogs" }),
          descriptionLabel: t("infoResource.projectDescription", { ns: "dialogs" }),
          loaderLabel: t("infoResource.uploadProjectImage", { ns: "dialogs" }),
        },
      }}
    >
      <DialogTitle titleText={styledHeaderText(alias, "свойства")} />
      <DialogContent>
        <RestrictedWidthContainer>
          <ProjectMetaBlock>
            <ResourceMeta meta={{ description, preview: { src: preview ? previewUrl : "" } }} readOnly>
              <DataField column>
                <FieldValue field={t("infoResource.owner", { ns: "dialogs" })} value={owner} />
                <FieldValue field={t("infoResource.totalLayers", { ns: "dialogs" })} value={formatNumber(layerCount)} />
                <FieldValue field={t("infoResource.projectSaved", { ns: "dialogs" })} value={formatDate(changedDate)} />
              </DataField>
            </ResourceMeta>
          </ProjectMetaBlock>
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        cancelButtonText={!projectIsOpened && t("actions.open", { ns: "common" })}
        onCancel={!projectIsOpened && onOpen}
      />
    </FullScreenDialog>
  );
};
